import { jwtDecode } from 'jwt-decode';
import { inject, Injectable, signal } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { GrowthBookService } from "src/app/core/services/growthbook.service";
import { NavService } from "src/app/core/services/nav.service";
import { SuppliersService } from "./suppliers.service";
import { Router } from '@angular/router';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { GibService } from './gib.service';
import { AccountsService } from './accounts.service';
import { skipHeaders } from '../interceptors/http-context-tokens';


@Injectable({
  providedIn: "root",
})
export class AuthService {
  private gibService = inject(GibService);
  private _authNavStatusSource = new BehaviorSubject<boolean>(false);
  authNavStatus$ = this._authNavStatusSource.asObservable();

  // private manager = new UserManager(getClientSettings());
  private user: User | null = {};
  private userProfile: UserProfile | null;
  userProfile$ = new BehaviorSubject<UserProfile | null>(null);

  public get accessToken(): string {
    return localStorage.getItem('accessToken')!;
    // return this.localManager.getData('accessToken');
  }

  public set accessToken(token) {
    localStorage.setItem('accessToken', token);
  }

  public get ccToken(): string {
    return localStorage.getItem('ccToken')!;
    // return this.localManager.getData('ccToken');
  }

  public set ccToken(token) {
    localStorage.setItem('ccToken', token);
  }

  public get refreshToken(): string {
    return localStorage.getItem('refreshToken')!;
    // return this.localManager.getData('accessToken');
  }

  public set refreshToken(token) {
    localStorage.setItem('refreshToken', token);
  }


  get _user(): User {
    return this.user;
  }

  get _userProfile(): UserProfile {
    return this.userProfile;
  }

  public deviceID = signal<string>("");

  constructor(
    private suppliersService: SuppliersService,
    private _http: HttpClient,
    private _gbs: GrowthBookService,
    private navService: NavService,
    private router: Router,
    private accountsService: AccountsService
  ) {
    // this.manager.clearStaleState();
    // this.manager.events.addSilentRenewError(() => {
    //   this.manager.signinSilent().catch(() => {
    //     this.signOut()
    //   });
    // });
    if (this.accessToken) {
      this.authenticateUser();
      this.gibService.setUserAuth();
    }
  }

  async authenticateUser(): Promise<void> {
    try {
      await this.setUserFromStorage();
      await this.setUserClaimsPermissionsAndMenuPromise(this.user);
    }
    catch (e) {
      console.error(e);
    }
  }


  isAuthenticated(): boolean {
    // return this.user != null && !this.user.expired;
    return this.user != null;
  }

  signIn() {
    // alert("Someone is calling SignIn of Oidc");
    // this.manager.signinRedirect();
  }

  signOut() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    this.user = {};
    this.userProfile = null;
    this.userProfile$.next(null);
    this._authNavStatusSource.next(this.isAuthenticated());
    this.router.navigate(['welcome']);
    this.gibService.setGuest();
    this.accountsService.reset();

    // clear out old user from the service on logout
    // alongside all tokens
    // this.manager.signoutRedirect();
  }

  // async silentRenew() {
  //   const config = {
  //     userStore: new Oidc.WebStorageStateStore({
  //       store: window.sessionStorage,
  //     }),
  //   };
  //   try {
  //     this.user = await new Oidc.UserManager(config).signinSilentCallback();
  //   } catch (error) {
  //     console.error(error);
  //   }
  // }

  setUser() {
    // this.manager.getUser().then((user) => {
    // localStorage.setItem("user", JSON.stringify(this.user));
    // this.user = user;
    // this._authNavStatusSource.next(this.isAuthenticated());
    // });
  }

  async setUserFromStorage(): Promise<User> {
    const token = jwtDecode(this.accessToken);
    // console.log(token);
    this.user.profile = token;
    // console.log(this.user);
    await this._gbs.loadGrowthBook(this.user.profile.email);
    return new Promise<User>((resolve, reject) => {
      resolve(this.user);
    });
  }

  async handleSignInCallBack() {
    try {
      // this.user = await this.manager.signinRedirectCallback();
      this.setUserClaimsPermissionsAndMenu(this.user);
    } catch (error) {
      console.error(error);
    }
  }

  async setUserClaimsPermissionsAndMenu(user: User): Promise<void> {
    this.userProfile = user;
    await this._gbs.loadGrowthBook(user.profile.email);
    const permissions = await this.getPermissions();
    const isSupervisor = this.findSupplierSupervisorRole(user?.profile["role"]);

    this.setUserInformation(
      permissions,
      isSupervisor,
      user.profile.role,
      user.profile.entityId
    );
    this._authNavStatusSource.next(this.isAuthenticated());
  }

  async getPermissions() {
    const response = await this.suppliersService
      .GetSupervisorsPermissions()
      .toPromise();
    return response?.permissions?.join(",");
  }

  checkAuthorizedUser(userRole: string | string[] = undefined): boolean {
    let role = userRole ?? this.userProfile?.profile?.role;
    return (typeof role === "string" && role === "SupplierAuthorizedUser") ||
      (Array.isArray(role) && role.includes("SupplierAuthorizedUser"));
  }

  findSupplierSupervisorRole(role: string | string[]): string {
    let isSupervisor =
      (typeof role === "string" && role === "SupplierSupervisor") ||
      (Array.isArray(role) && role.includes("SupplierSupervisor"));
    let authorizedUser = this.checkAuthorizedUser(role);
    let isGodUser =
      (typeof role === "string" && role === "SupplierGodUser") ||
      (Array.isArray(role) && role.includes("SupplierGodUser"));

    if (isGodUser || authorizedUser) return "false";
    if (isSupervisor) return "true";
    return null;
  }
  // Handle the case where user refreshes and permissions are loaded after the route guard is activated
  async setUserClaimsPermissionsAndMenuPromise(user: User): Promise<void> {
    this.userProfile = this.user;

    return new Promise<void>(async (resolve, reject) => {
      try {
        const permissions = await this.getPermissions();
        const isSupervisor = this.findSupplierSupervisorRole(
          user.profile["role"]
        );
        this.setUserInformation(
          permissions,
          isSupervisor,
          user.profile.role,
          user.profile.entityId
        );
        this._authNavStatusSource.next(this.isAuthenticated());
        resolve();
      } catch (e) {
        this.signOut();
        reject(e);
      }
    });
  }

  setUserRoles(roles, isSupervisor): string[] {
    // console.log(isSupervisor, typeof (isSupervisor));
    if (isSupervisor === "false") return ["god_user"];
    else if (!isSupervisor) {
      this.signOut();
    }
    let userRoles: string[] = [];
    if (roles) {
      if (typeof roles === "string") userRoles.push(...roles.split('"'));
      userRoles.push(...roles);
    } else userRoles = roles;
    return userRoles;
  }

  setUserInformation(permissions, isSupervisor, roles, entityId) {
    console.log("setting userinfo");
    this.userProfile.userRoles = this.setUserRoles(roles, isSupervisor) || [];
    this.userProfile.userPermissions = this.setUserPermissions(
      permissions,
      isSupervisor
    );
    this.userProfile.entityId = entityId;
    this.setUserMenu(
      this.userProfile.userPermissions,
      this.userProfile.userRoles
    );
    this.userProfile.GodUser = isSupervisor === "false";
    this.userProfile.AuthorizedUser = this.checkAuthorizedUser();
    this.userProfile$.next(this.userProfile);
    this.setDeviceUUID();
  }

  setDeviceUUID() {
    let deviceUUID = localStorage.getItem("deviceUUID");
    if (!deviceUUID) {
      deviceUUID = window.crypto.randomUUID();
      localStorage.setItem("deviceUUID", deviceUUID);
    }
    this.deviceID.set(deviceUUID);
  }

  setUserMenu(permissions, roles) {
    // console.log("setting user menu", permissions, roles);

    this.navService.setUserMenu(roles, permissions);
  }

  setUserPermissions(permissions, isSupervisor): string[] {
    let userPermissions: string[] = [];
    if (permissions) {
      if (typeof permissions === "string")
        userPermissions.push(...permissions.split(","));
    }
    return userPermissions;
  }


  public refreshTokenAction() {

    let body = new HttpParams();
    body = body.set('grant_type', 'refresh_token');
    body = body.set('client_id', environment.variables.login.client_id);
    body = body.set('client_secret', environment.variables.login.client_secret);
    body = body.set('refresh_token', this.refreshToken);
    body = body.set('scope', environment.variables.login.scope);

    return this.refreshTokenApi(body.toString());
  }

  refreshTokenApi(data: any): Observable<any> {
    const baseUrl = environment.baseAPIUrl;
    return this._http.post<any>(`${baseUrl}/connect/token`, data, {
      context: skipHeaders(), headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
      }
    });
  }
}

export enum PermissionsEnum {
  NO_PERMISSIONS_FOUND = 1,
  API_ERROR,
}
export class User {
  profile?: {
    amr?: string[],
    auth_time?: number,
    deviceUUID?: string,
    email?: string,
    email_verified?: boolean,
    entityId?: string,
    idp?: string,
    isSupervisor?: string,
    name?: string,
    phone_number?: string,
    phone_number_verified?: boolean,
    preferred_username?: string,
    role?: string[],
    sid?: string,
    sub?: string
  }
  // access_token: string;
  // refresh_token: string;
  // token_type: string;
  // expires_at: number;
  // scope: string;
  // expired: boolean;
}

export class UserProfile extends User {
  userRoles?: string[];
  userPermissions?: string[];
  entityId?: number;
  GodUser?: boolean;
  AuthorizedUser?: boolean;
}

// export function getClientSettings(): UserManagerSettings {
//   return {
//     authority: window["env"]["IS_BASE_URL"] || "https://accounts-stg.hala.com",
//     client_id: "halalah_suppliers_portal",
//     redirect_uri: `${window.origin}/auth-callback`,
//     post_logout_redirect_uri: window.origin,
//     response_type: "id_token token",
//     scope: "openid profile roles scope_payments_gw_api scope_user_gw_api scope_web_users_gw_api",
//     loadUserInfo: true,
//     accessTokenExpiringNotificationTime: 60,
//     automaticSilentRenew: true,
//     silent_redirect_uri: `${window.origin}/auth-callback`,
//   };
// }
